// Client-side mirror of the API's investment future-value solver
// (api/src/Framework/Utils/goalProjections.ts). Kept in sync so the investment
// goal's predicted achievement date can be recomputed live when the assumed
// return slider moves, without a server round-trip. Amounts are in euros here
// (the ratio math is unit-agnostic).

const MAX_PROJECTION_MONTHS = 200 * 12;

export interface InvestmentProjectionInput {
  currentValue: number;
  target: number;
  annualReturn: number; // decimal, e.g. 0.07
  contribution: number; // per period
  periodsPerYear: number; // 12 (monthly) or 1 (yearly)
}

export interface InvestmentProjectionResult {
  reached: boolean;
  reachable: boolean;
  monthsToTarget: number | null;
}

export const projectInvestmentGoal = (
  input: InvestmentProjectionInput,
): InvestmentProjectionResult => {
  const { currentValue, target, annualReturn, contribution, periodsPerYear } =
    input;
  const monthsPerPeriod = 12 / periodsPerYear;

  if (currentValue >= target) {
    return { reached: true, reachable: true, monthsToTarget: 0 };
  }

  const unreachable: InvestmentProjectionResult = {
    reached: false,
    reachable: false,
    monthsToTarget: null,
  };
  const i = annualReturn / periodsPerYear;

  let periods: number;
  if (i === 0) {
    if (contribution <= 0) {
      return unreachable;
    }
    periods = (target - currentValue) / contribution;
  } else {
    const g = 1 + i;
    if (g <= 0) {
      return unreachable;
    }
    const base = currentValue + contribution / i;
    if (base <= 0) {
      return unreachable;
    }
    const ratio = (target + contribution / i) / base;
    if (ratio <= 1) {
      return { reached: false, reachable: true, monthsToTarget: 0 };
    }
    periods = Math.log(ratio) / Math.log(g);
  }

  if (!Number.isFinite(periods) || periods < 0) {
    return unreachable;
  }

  const months = Math.ceil(periods * monthsPerPeriod);
  if (months > MAX_PROJECTION_MONTHS) {
    return unreachable;
  }

  return { reached: false, reachable: true, monthsToTarget: months };
};

// Formats a months-from-now offset into a YYYY-MM-DD predicted date, or null
// when the goal is unreachable at the assumed return.
export const predictedDateFromMonths = (
  monthsToTarget: number | null,
): string | null => {
  if (monthsToTarget === null) {
    return null;
  }
  const date = new Date();
  date.setMonth(date.getMonth() + monthsToTarget);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return year + "-" + month + "-" + day;
};
